<script>
    import { onMount } from "svelte";
    import { isMobile } from "../../stores";
    import {
        SearchInput,
        SvelteTable,
        PageLoading,
        SearchSelect,
    } from "../../Components/Common/index.svelte";
    import TableToggle from "../../Components/Common/TableToggle.svelte";
    import { apiMethods } from "../../Services/index.svelte";
    import {
        replaceURL,
        getText,
        getHospitalType,
        goToURL,
        showError,
        decodeSearchParams,
        encodeSearchParams,
        getBoolean,
    } from "../../Utils/utilFunctions.svelte";
    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";
    import { isEnvNewOrApp } from "../../Utils/utilRoute.svelte";

    export let params = {};

    let isGettingHospitals = false,
        returnHospitalsArr = [],
        filters = { state: "all" },
        page = 0,
        pageSize = 25,
        count;

    const columns = [
        {
            key: "name",
            title: "Name",
            headerClass:
                "text-left px-3 w-3/12 sortable-header-decoration rounded-tl-xl",
            class: "text-left px-3 w-3/12 cursor-pointer",
            renderValue: (v) => getText(v.name),
        },
        {
            key: "type",
            title: "Type",
            headerClass: "text-left px-3 w-1/12 cursor-default",
            class: "text-left px-3 w-1/12 cursor-pointer",
            renderValue: (v) => getHospitalType(v.hospitalType),
        },
        {
            key: "providerNum",
            title: "Provider Number",
            headerClass: "text-left px-3 w-1/12 cursor-default",
            class: "text-left px-3 w-1/12 cursor-pointer",
            renderValue: (v) =>
                v.providerNumber ? getText(v.providerNumber) : "-",
        },
        {
            key: "email",
            title: "Email",
            headerClass: "text-left px-3 w-2/12 cursor-default",
            class: "text-left px-3 w-2/12 cursor-pointer",
            renderValue: (v) => _getHospitalEmail(v.email),
            csvContent: (v) => _getHospitalEmail(v.email, true),
        },
        isEnvNewOrApp() ?
        {
            key: "isNoGapProgram",
            title: "Has No-Gap Program",
            headerClass: "text-left px-3 w-2/12 cursor-default",
            class: (v) =>
                `text-left pl-4 text-[14px] cursor-auto ${
                    v.isDeleted ? "opacity-60" : ""
                }`,
            renderValue: (v) => v.isNoGapProgram ? "Yes" : "No",
        } : null,
        {
            key: "address",
            title: "Address",
            headerClass: "text-left px-3 w-2/12 cursor-default",
            class: "text-left px-3 w-2/12 cursor-pointer",
            renderValue: (v) => (v.address ? getText(v.address) : "-"),
        },
        {
            key: "suburb",
            title: "Suburb",
            headerClass: "text-left px-3 w-1/12 cursor-default",
            class: "text-left px-3 w-1/12 cursor-pointer",
            renderValue: (v) => (v.suburb ? getText(v.suburb) : "-"),
        },
        {
            key: "state",
            title: "State",
            headerClass: "text-left px-3 w-1/12 cursor-default",
            class: "text-left px-3 w-1/12 cursor-pointer",
            renderValue: (v) => (v.state ? getText(v.state) : "-"),
        },
        {
            key: "postcode",
            title: "Postcode",
            headerClass: "text-left px-3 w-1/12 cursor-default rounded-tr-xl",
            class: "text-left px-3 w-1/12 cursor-pointer",
            renderValue: (v) => (v.postcode ? getText(v.postcode) : "-"),
        },
    ];

    const sortableHeaderKeys = ["name"];

    onMount(() => {
        if (params.query) updateSearchParamsByQuery(params.query);

        loadHospitals(page * pageSize, pageSize, _getSearchParams());
    });

    const updateSearchParamsByQuery = (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("search="))
                    filters.search = arr[i].split("=")[1].toString();

                if (arr[i].includes("state="))
                    filters.state = arr[i].split("=")[1].toString();

                if (arr[i].includes("sort="))
                    filters.sort = arr[i].split("=")[1].toString();
            }

            updateURL();
        }
    };

    const loadHospitals = async (start, limit, params) => {
        isGettingHospitals = true;
        if (params.sort) params.sort = params.sort.toLowerCase();

        await apiMethods.getMethod
            .getHospitals(start, limit, params)
            .then((res) => _handleHospitals(res))
            .then(() => _updateHeaderSortIcon());

        isGettingHospitals = false;
    };

    const handleFilterSelect = () => {
        page = 0;

        updateURL();

        loadHospitals(0, pageSize, _getSearchParams());
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (key == "email" && rowData.email)
            window.open(`mailto:${rowData.email}`);
        else if (rowData.id && key != "isNoGapGroup")
            goToURL(`/Hospitals/view/${rowData.id}`);
    };

    const handleClickCol = ({ target: { className } }, rowData, key) => {
        if (sortableHeaderKeys.includes(key)) {
            if (filters.sort == key) filters.sort = `${key}Desc`;
            else filters.sort = key;
            updateURL();
            loadHospitals(0, pageSize, _getSearchParams());
        }
    };

    const _updateHeaderSortIcon = () => {
        let thRow = document.getElementsByTagName("th");
        const params = _getSearchParams();

        if (params) {
            setTimeout(() => {
                const upSpan = document.createElement("span");
                const downSpan = document.createElement("span");
                upSpan.innerHTML = `<i class="fas fa-sort-alpha-down" />`;
                upSpan.style = "font-size: 10px;";
                downSpan.innerHTML = `<i class="fas fa-sort-alpha-up-alt" />`;
                downSpan.style = "font-size: 10px;";

                Array.from(thRow).forEach((row) => {
                    if (
                        params.sort == "name" &&
                        row.childNodes[0].data == "Name"
                    )
                        row.appendChild(upSpan);
                    else if (
                        params.sort == "nameDesc" &&
                        row.childNodes[0].data == "Name"
                    )
                        row.appendChild(downSpan);
                });
            }, 0);
        }
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadHospitals(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadHospitals(0, newPageSize, _getSearchParams());
    };

    const updateURL = () => {
        replaceURL(
            `/Hospitals/page=${page}&size=${pageSize}${_getParamsString()}`,
        );
    };

    // HELPER FUNCTIONS
    const _handleHospitals = async (res) => {
        if (res.error) showError(res.error);
        else {
            returnHospitalsArr = [...res.results];
            count = res.total;
        }
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters.sort) params.sort = filters.sort;

        if (filters.search) params.search = filters.search;

        if (filters.state && filters.state !== "all")
            params["filter[state]"] = filters.state;

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params.search) str += `&search=${params.search}`;

            if (params["filter[state]"])
                str += `&state=${params["filter[state]"]}`;
        }

        return encodeSearchParams(str);
    };

    const _getHospitalEmail = (email, isCsvContent = false) => {
        if (!email) return "-";

        return isCsvContent
            ? `${email}`
            : `<div class="emailLink cursor-pointer hover:underline">${email}</div>`;
    };
</script>

<div class="flex flex-col flex-1 gap-y-4">
    <div class="flex flex-col gap-y-4 flex-0">
        <div class="flex flex-row items-center justify-between flex-wrap">
            <div class="flex gap-4 flex-wrap">
                <SearchInput
                    bind:value={filters.search}
                    title="Search"
                    inputFunc={handleFilterSelect}
                    isLong
                />

                <SearchSelect
                    title="State"
                    bind:value={filters.state}
                    onChangeFunc={handleFilterSelect}
                    isLong
                >
                    <option value="all">All</option>
                    <option value="VIC">Victoria</option>
                    <option value="NSW">New South Wales</option>
                    <option value="QLD">Queensland</option>
                    <option value="TAS">Tasmania</option>
                    <option value="WA">Western Australia</option>
                    <option value="SA">South Australia</option>
                    <option value="NT">Northern Territory</option>
                    <option value="ACT">Australian Capital Territory</option>
                </SearchSelect>
            </div>
        </div>
    </div>

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isGettingHospitals}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
            >
                <SvelteTable
                    columns={columns.filter((c) => c != null)}
                    rows={returnHospitalsArr}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                    customRow="align-middle"
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={returnHospitalsArr}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns,
            data: returnHospitalsArr,
            fileName: "Hospitals",
            allDataApi: async () =>
                await apiMethods.getMethod.getHospitals(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>
